export interface TodayMenuStatus {
  status: 'up_to_date' | 'updating' | 'delayed' | 'unavailable'
  last_updated_at: string | null
  targets?: Array<{ id?: string; kind?: 'competitor' | 'own_location'; last_updated_at: string | null; status: string }>
}

function timestampMs(value: string | null): number {
  return value ? Date.parse(value.replace(' ', 'T').replace(/(\.\d{3})\d+/, '$1').replace(/([+-]\d{2})$/, '$1:00')) : NaN
}

export function todayRefreshSummary(statuses: TodayMenuStatus[]) {
  // Today concerns rival activity; owned-menu history must not date this control.
  const timestamps = statuses.flatMap(s => s.targets?.length
    ? s.targets.filter(t => t.kind === 'competitor').map(t => t.last_updated_at)
    : [s.last_updated_at],
  ).flatMap(value => {
    if (!value) return []
    // Postgres text timestamps need ISO separators/offsets for mobile browsers.
    const parsed = new Date(timestampMs(value))
    return Number.isFinite(parsed.getTime()) ? [parsed.toISOString()] : []
  })
  return {
    updating: statuses.some(s => s.status === 'updating'),
    needsReview: statuses.some(s => s.status === 'unavailable'),
    lastUpdated: timestamps.length
      ? timestamps.reduce((a, b) => Date.parse(a) > Date.parse(b) ? a : b)
      : null,
  }
}

type FetchFn = (url: string, init?: RequestInit) => Promise<Response>

export async function queueTodayRefresh(ids: string[], fetcher: FetchFn, api: string) {
  const results = await Promise.allSettled([...new Set(ids)].map(async id => {
    const response = await fetcher(`${api}/api/v1/menu-refresh/locations/${encodeURIComponent(id)}`, { method: 'POST' })
    if (!response.ok) throw new Error('Update request failed')
    const body = await response.json()
    return Number(body.queued || 0) + Number(body.already_queued || 0)
  }))
  return {
    pending: results.some(r => r.status === 'fulfilled' && r.value > 0),
    failed: results.filter(r => r.status === 'rejected').length,
  }
}

// Server jobs can be accepted before their first running status is visible.
// Wait for every previously available target, rather than treating that gap as completion.
export function todayRefreshFinished(before: TodayMenuStatus[], after: TodayMenuStatus[]): boolean {
  if (!before.length || before.length !== after.length) return false
  return before.every((location, index) => {
    const next = after[index]
    if (next.status === 'updating') return false
    if (location.targets?.length) {
      return location.targets.filter(t => t.status !== 'unavailable').every(target => {
        const updated = next.targets?.find(t => t.id === target.id && t.kind === target.kind)
        if (!updated) return false
        if (updated.status === 'unavailable') return true
        return !!updated.last_updated_at && (!target.last_updated_at ||
          timestampMs(updated.last_updated_at) > timestampMs(target.last_updated_at))
      })
    }
    return next.status === 'unavailable' || (!!next.last_updated_at &&
      (!location.last_updated_at || timestampMs(next.last_updated_at) > timestampMs(location.last_updated_at)))
  })
}
